import { useEffect, useRef, useState } from 'react';
import CategoryTabs from './components/CategoryTabs';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import TypeTabs from './components/TypeTabs';
import { CATEGORY_TAB_WIDTH, HEADER_HEIGHT } from '../../_constants/styleConst';
import InspectionHeader from './components/InspectionHeader';
import InspectionMain from './components/InspectionMain/InspectionMain';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import { fetchTypeLabels } from '../../stores/TypeLabels/slice';
import {
  fetchInspectionTypeData,
  updateStatus,
  updateJudgement,
} from '../../stores/inspectionTypeData/slice';
import {
  getStatusInspectionOfType,
  isComplete,
  isEmpty,
  isNg,
  calcCategoryProgress,
  isInputAutoLeakTest,
} from '../../_utils/inspectionUtils';
import {
  fetchInspectionTop,
  updateCategoryProgress,
} from '../../stores/inspectionTop/slice';
import InspectionManual from './components/InspectionManual';
import { setCategoryId, setTypeId } from '../../stores/selectIds/slice';
import {
  CONFIRMED_FLOODING_TEST,
  LG_CONNECTOR_TEST,
  JUDGEMENT,
  LPA_TEST,
  DTA_IMAGE_INSPECTION,
} from '../../_constants/inspectionConst';
import ConfirmModal from '../../_common/Modal/ConfirmModal';
import { useTranslation } from 'react-i18next';
import { LEAK_TEST } from '../../_constants/inspectionConst';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import MemoTextArea from './components/MemoTextArea';
import { isInspection } from '../../_utils/utils';
import { InspectionType } from '../../services/apiTypes';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import {
  isGetInspectionTop,
  isGetInspectionType,
  isGetTypeLabelList,
} from '../../_utils/typeGuardUtils';
import { API_CODE } from '../../_constants/apiConst';

export type RejectedTypeList = {
  newInspectionType: InspectionType;
  isInspection: boolean;
};

/** 診断画面 */
const Inspection: React.FC = () => {
  const { id } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);
  const { inspectionTypeDataMap, before } = useSelector(
    (state: RootState) => state.inspectionTypeData,
  );
  const { error, showApiError, showTypeError, hideError } = useErrorModal();

  const categoryTypeLabel = typeLabelsMap[categoryId];

  // 遷移するときに確認のダイアログを表示
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  // 遷移するタブのID
  const [newId, setNewId] = useState<{
    newCategoryId: string;
    newTypeId: string;
  }>({ newCategoryId: '', newTypeId: '' });
  // 更新対象のTypeId(更新直後に参照したいためRefで管理)
  const targetsToUpdateTypeSet = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (id) {
      dispatch(setCategoryId(id));
    }

    if (repairRequest && inspectionTop.rmaNo === '') {
      fetchTopData(repairRequest.rmaNo, isInspection(repairRequest.status));
    }
  }, [repairRequest]);

  // 中項目ラベルを取得
  useEffect(() => {
    inspectionTop.inspectionCategoryList.forEach(async (category) => {
      if (repairRequest && !typeLabelsMap[category.id]) {
        const response = await dispatch(
          fetchTypeLabels({
            query: {
              rmaNo: repairRequest.rmaNo,
              categoryId: category.id,
            },
            isInspection: isInspection(repairRequest.status),
          }),
        );

        const apiCode = isInspection(repairRequest.status)
          ? API_CODE.INSPECTION_TYPE_LABELS
          : API_CODE.QC_TYPE_LABELS;
        if (isRejected(response)) {
          showApiError(response.payload, apiCode, () =>
            navigate(INSPEX_PATHNAME.INSPECTION_TOP),
          );
          return;
        }
        if (!isGetTypeLabelList(response.payload)) {
          showTypeError(apiCode);
          return;
        }
        // 初回読み込み時、先頭の中項目をセットする
        if (category.id === id) {
          dispatch(setTypeId(response.payload[0].id));
        }
      }
    });
  }, [
    repairRequest,
    categoryTypeLabel,
    inspectionTop.inspectionCategoryList.length,
  ]);

  useEffect(() => {
    // 診断データを取得
    if (
      repairRequest &&
      typeLabelsMap[categoryId] &&
      typeLabelsMap[categoryId].length > 0
    ) {
      typeLabelsMap[categoryId].forEach(async (type) => {
        if (
          isInspection(repairRequest.status) &&
          (!inspectionTypeDataMap[categoryId] ||
            !inspectionTypeDataMap[categoryId][type.id])
        ) {
          const response = await dispatch(
            fetchInspectionTypeData({
              rmaNo: repairRequest.rmaNo,
              categoryId,
              typeId: type.id,
            }),
          );

          if (isFulfilled(response)) {
            if (!isGetInspectionType(response.payload)) {
              showTypeError(API_CODE.INSPECTION_TYPE);
            }
          } else {
            showApiError(response.payload, API_CODE.INSPECTION_TYPE, () =>
              navigate(INSPEX_PATHNAME.INSPECTION_TOP),
            );
          }

          // 連動元であれば浸水確認のデータも取得
          if (
            !inspectionTypeDataMap[CONFIRMED_FLOODING_TEST.categoryId] &&
            (categoryId === LG_CONNECTOR_TEST.categoryId ||
              categoryId === LPA_TEST.categoryId)
          ) {
            const res = await dispatch(
              fetchInspectionTypeData({
                rmaNo: repairRequest.rmaNo,
                categoryId: CONFIRMED_FLOODING_TEST.categoryId,
                typeId: CONFIRMED_FLOODING_TEST.typeId,
              }),
            );

            if (isFulfilled(res)) {
              if (!isGetInspectionType(res.payload)) {
                showTypeError(API_CODE.INSPECTION_TYPE);
              }
            } else {
              showApiError(res.payload, API_CODE.INSPECTION_TYPE, () =>
                navigate(INSPEX_PATHNAME.INSPECTION_TOP),
              );
            }
          }
        }
      });
    }
  }, [repairRequest, categoryId, typeLabelsMap[categoryId]]);

  /** 小分類を変更する時、 大項目の進捗率も更新される*/
  useEffect(() => {
    if (
      !inspectionTypeDataMap[categoryId] ||
      !inspectionTypeDataMap[categoryId][typeId]
    ) {
      return;
    }
    const status = getStatusInspectionOfType(
      inspectionTypeDataMap[categoryId][typeId].itemData,
    );

    let completeNum = 0;
    for (const key in inspectionTypeDataMap[categoryId]) {
      if (key === typeId) {
        completeNum = isComplete(status) ? completeNum + 1 : completeNum;
      } else if (
        inspectionTypeDataMap[categoryId][key] &&
        isComplete(inspectionTypeDataMap[categoryId][key].status)
      ) {
        completeNum++;
      }
    }

    const newProgress = calcCategoryProgress(
      completeNum,
      typeLabelsMap[categoryId].length,
    );

    dispatch(
      updateCategoryProgress({
        id: categoryId,
        progress: newProgress,
      }),
    );
  }, [inspectionTypeDataMap]);

  // 結果入力画面でリロード時にアラートを表示
  useEffect(() => {
    // beforeunloadイベントのハンドラ
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // ブラウザの標準的な動作をキャンセル
      event.preventDefault();
    };

    // イベントリスナーをwindowに追加
    window.addEventListener('beforeunload', handleBeforeUnload);

    // クリーンアップ関数: コンポーネントがアンマウントされるときにイベントリスナーを削除
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // ブラウザの戻る・進むボタンを無効にする
  useEffect(() => {
    window.history.pushState(null, '', null);
    const handlePopState = () => {
      window.history.go(1);
      return;
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 診断Topデータを取得
  const fetchTopData = async (rmaNo: string, isInspection: boolean) => {
    const response = await dispatch(
      fetchInspectionTop({ query: { rmaNo }, isInspection }),
    );

    const apiCode = isInspection ? API_CODE.INSPECTION_TOP : API_CODE.QC_TOP;
    if (isRejected(response)) {
      showApiError(response.payload, apiCode, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
      return;
    } else if (!isGetInspectionTop(response.payload)) {
      showTypeError(apiCode);
      return;
    }
  };

  /** PUT対象の中項目IDをセット */
  const setTargetTypes = (beforeTypeId: string) => {
    if (beforeTypeId) {
      // 存在する中項目の一覧
      const existTypeList = Object.keys(typeLabelsMap)
        .map((categoryId) => typeLabelsMap[categoryId].map((type) => type.id))
        .flat();

      const newUpdateTypeList = new Set(targetsToUpdateTypeSet.current);
      // 遷移前の中項目を追加
      newUpdateTypeList.add(beforeTypeId);

      // 連動する中項目があれば入れておく
      // 先端部画像を表示する中項目
      if (DTA_IMAGE_INSPECTION.typeIdList.includes(beforeTypeId)) {
        DTA_IMAGE_INSPECTION.typeIdList.forEach((typeId) => {
          // 存在チェック
          if (existTypeList.includes(typeId)) {
            newUpdateTypeList.add(typeId);
          }
        });
      }
      // 浸水確認へ連携する中項目
      if (
        beforeTypeId === LEAK_TEST.typeId ||
        beforeTypeId === LG_CONNECTOR_TEST.typeId ||
        beforeTypeId === LPA_TEST.typeId
      ) {
        // 存在チェック
        if (existTypeList.includes(CONFIRMED_FLOODING_TEST.typeId)) {
          newUpdateTypeList.add(CONFIRMED_FLOODING_TEST.typeId);
        }
      }
      targetsToUpdateTypeSet.current = newUpdateTypeList;
    }
  };

  /** 気密検査 → 浸水確認のNA自動入力 */
  const updateLeakTestAndFloodingTest = () => {
    const leakTestData =
      inspectionTypeDataMap[LEAK_TEST.categoryId][LEAK_TEST.typeId];
    const floodingTestData =
      inspectionTypeDataMap[CONFIRMED_FLOODING_TEST.categoryId]?.[
        CONFIRMED_FLOODING_TEST.typeId
      ];

    // 乾式気密検査or水没気密検査にN/Aを入力
    leakTestData.itemData.forEach((item) => {
      if (isEmpty(item.judgement)) {
        dispatch(
          updateJudgement({
            categoryId: LEAK_TEST.categoryId,
            typeId: LEAK_TEST.typeId,
            path: [item.id],
            judgement: JUDGEMENT.NA,
          }),
        );
      }
    });
    // 浸水確認の空の項目にN/Aを入力
    floodingTestData?.itemData.forEach((item) => {
      if (isEmpty(item.judgement)) {
        dispatch(
          updateJudgement({
            categoryId: CONFIRMED_FLOODING_TEST.categoryId,
            typeId: CONFIRMED_FLOODING_TEST.typeId,
            path: [item.id],
            judgement: JUDGEMENT.NA,
          }),
        );
      }
    });

    dispatch(
      updateStatus({
        categoryId: CONFIRMED_FLOODING_TEST.categoryId,
        typeId: CONFIRMED_FLOODING_TEST.typeId,
        status: 2,
      }),
    );

    dispatch(
      updateCategoryProgress({ id: LEAK_TEST.categoryId, progress: 100 }),
    );
  };

  /** LGコネクタ・LG棒 > 水滴 → 浸水確認への連動 */
  const linkLGConnector = () => {
    // 連動先の浸水確認のデータ
    const floodingTestData =
      inspectionTypeDataMap[CONFIRMED_FLOODING_TEST.categoryId]?.[
        CONFIRMED_FLOODING_TEST.typeId
      ];
    if (!floodingTestData) {
      return;
    }

    const currentTypeData = inspectionTypeDataMap[categoryId][typeId];
    // 水滴の項目
    const moistureBead = currentTypeData.itemData
      .find((problem) => {
        return problem.itemData.find(
          (item) =>
            item.id === LG_CONNECTOR_TEST.itemId || item.id === LPA_TEST.itemId,
        );
      })
      ?.itemData.find(
        (item) =>
          item.id === LG_CONNECTOR_TEST.itemId || item.id === LPA_TEST.itemId,
      );

    // 水滴の結果がNGであれば、浸水確認のLGコネクタをNGで上書き
    if (!moistureBead || !isNg(moistureBead.judgement)) {
      return;
    }

    dispatch(
      updateJudgement({
        categoryId: CONFIRMED_FLOODING_TEST.categoryId,
        typeId: CONFIRMED_FLOODING_TEST.typeId,
        path: [CONFIRMED_FLOODING_TEST.lgConnectorId],
        judgement: JUDGEMENT.NG,
      }),
    );

    // 気密検査の進捗率を更新
    if (floodingTestData.status === 2) {
      // 既に完了済みの場合は不要
      return;
    }

    // LGコネクタ以外が空でなければ完了
    const status = floodingTestData.itemData.every(
      (item) =>
        item.id === CONFIRMED_FLOODING_TEST.lgConnectorId ||
        !isEmpty(item.judgement),
    )
      ? 2
      : 1;
    dispatch(
      updateStatus({
        categoryId: CONFIRMED_FLOODING_TEST.categoryId,
        typeId: CONFIRMED_FLOODING_TEST.typeId,
        status,
      }),
    );

    if (status === 2) {
      const categoryData =
        inspectionTypeDataMap[CONFIRMED_FLOODING_TEST.categoryId];
      let completeNum = 1; // 浸水確認は完了なので1からスタート
      // それ以外の完了数をカウント
      for (const key in categoryData) {
        if (key !== typeId && isComplete(categoryData[key].status)) {
          completeNum++;
        }
      }
      const newProgress = calcCategoryProgress(
        completeNum,
        typeLabelsMap[CONFIRMED_FLOODING_TEST.categoryId].length,
      );
      dispatch(
        updateCategoryProgress({
          id: CONFIRMED_FLOODING_TEST.categoryId,
          progress: newProgress,
        }),
      );
    }
  };

  /** 前へ/次へボタン・大/中項目タブ押下での中項目遷移時 */
  const changeType = (newCategoryId: string, newTypeId: string) => {
    const status = updateInputStatus();

    // 未完了の場合は確認ダイアログを表示
    if (status === 1) {
      setNewId({ newTypeId, newCategoryId });
      setIsOpenConfirmModal(true);
      return;
    }

    // 未入力or入力完了の場合はタブを切り替える
    dispatch(setTypeId(newTypeId));
    if (categoryId !== newCategoryId) {
      dispatch(setCategoryId(newCategoryId));
      navigate(INSPEX_PATHNAME.INSPECTION + newCategoryId);
    }
  };

  /**
   * 画面を離れる時の共通処理
   * 更新対象の中項目を更新、結果連動処理、入力ステータス更新
   */
  const updateInputStatus = () => {
    if (!repairRequest) {
      return 0;
    }

    // 更新対象の中項目を更新
    setTargetTypes(typeId);

    const { itemData: currentTypeData } =
      inspectionTypeDataMap[categoryId][typeId];

    let status: 0 | 1 | 2 = getStatusInspectionOfType(currentTypeData);

    // 中項目間の結果連動処理
    switch (typeId) {
      case LEAK_TEST.typeId:
        if (
          isInspection(repairRequest.status) && // 受入診断のみ実行
          isInputAutoLeakTest(currentTypeData)
        ) {
          status = 2;
          updateLeakTestAndFloodingTest();
        }
        break;
      case LG_CONNECTOR_TEST.typeId:
      case LPA_TEST.typeId:
        if (status === 2) {
          linkLGConnector();
        }
        break;
      default:
        break;
    }

    // 入力完了ステータス更新
    dispatch(updateStatus({ categoryId, typeId, status }));

    // 呼び出し元の後続処理のため最終的なステータスを返す
    return status;
  };

  /** 確認のダイアログのキャンセルイベント */
  const onClickCancel = () => {
    setIsOpenConfirmModal(false);
  };

  /** 確認のダイアログのOKイベント */
  const onClickOk = () => {
    dispatch(setTypeId(newId.newTypeId));
    if (newId.newCategoryId !== categoryId) {
      dispatch(setCategoryId(newId.newCategoryId));
      navigate(INSPEX_PATHNAME.INSPECTION + newId.newCategoryId);
    }
    setIsOpenConfirmModal(false);
  };

  return (
    <Box>
      <BasicInfo />
      <Box display="flex" flexDirection="column" p={0}>
        {repairRequest && typeLabelsMap[categoryId] && (
          <Box display="flex" flex="1 1 auto" gap={0}>
            <Box
              width={`${CATEGORY_TAB_WIDTH}`}
              p={0}
              overflow="auto"
              height={`calc(100dvh - ${HEADER_HEIGHT})`}
            >
              <CategoryTabs
                changeType={changeType}
                updateInputStatus={updateInputStatus}
                targetsToUpdateTypeSet={targetsToUpdateTypeSet}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              flex="1"
              mt={5}
              ml={'1rem'}
            >
              <TypeTabs changeType={changeType} />
              {inspectionTypeDataMap[categoryId] &&
                inspectionTypeDataMap[categoryId][typeId] &&
                inspectionTypeDataMap[categoryId][typeId].itemData.length >
                  0 && (
                  <Box display="flex">
                    <Box
                      width="60%"
                      mt={8}
                      display="flex"
                      flexDirection="column"
                      height={`calc(100dvh - ${HEADER_HEIGHT} - 6.5rem)`}
                    >
                      <InspectionHeader
                        itemDataList={
                          inspectionTypeDataMap[categoryId][typeId].itemData
                        }
                        displayType={
                          inspectionTypeDataMap[categoryId][typeId].displayType
                        }
                        changeType={changeType}
                        isInputAutoLeakTest={isInputAutoLeakTest}
                      />
                      <Box
                        sx={{
                          overflowY: 'auto',
                          mb: 1,
                        }}
                      >
                        <InspectionMain
                          typeLabel={
                            typeLabelsMap[categoryId].find(
                              (typeData) => typeData.id === typeId,
                            )?.label ?? ''
                          }
                          typeData={inspectionTypeDataMap[categoryId][typeId]}
                          beforeItemDataList={
                            before[categoryId] &&
                            before[categoryId][typeId] &&
                            !isInspection(repairRequest.status)
                              ? before[categoryId][typeId].itemData
                              : []
                          }
                        />
                        <MemoTextArea
                          memo={inspectionTypeDataMap[categoryId][typeId].memo}
                        />
                      </Box>
                    </Box>
                    <Box width="1%" mt={8} />
                    <Box width="39%" mt={8}>
                      <InspectionManual
                        ngSampleList={
                          inspectionTypeDataMap[categoryId][typeId].ngSample
                        }
                        manualPath={
                          inspectionTypeDataMap[categoryId][typeId].manualPath
                        }
                      />
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmModal
        isOpen={isOpenConfirmModal}
        displayMessage={t('MSG_UNFILLED_FIELD')}
        onClickCancel={onClickCancel}
        onClickOk={onClickOk}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default Inspection;
